"use client"

import React from "react"
import { Minus, Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import { ButtonGroup } from "@/shadcn/components/ui/button-group"

// Stepper Card. Figma component set: S311lGXE6JSyTnNAXHmtR7 node 27875:564
// (States: Default / Changed / Saving / Success / TierMax / Error / Disabled).
// A label + current value + a +/- Number Stepper; editing states add a monthly
// price, a footer of Cancel + primary action, and a colored border.

export type StepperState =
  | "default"
  | "changed"
  | "saving"
  | "success"
  | "tierMax"
  | "error"
  | "disabled"

export type StepperCardData = {
  label: string
  value: string | number
  /** Rendered in place of `value`; the stepper keeps counting in `value` units. */
  displayValue?: React.ReactNode
  state?: StepperState
  /** Monthly price shown in the editing states (changed / saving / error). */
  price?: string
  /** Message shown in the error state. */
  errorMessage?: string
  /** Helper line shown in the tierMax state, with an inline upgrade link. */
  tierMaxNote?: { before: string; cta: string; after: string }
  className?: string
  onClick: (qty: number) => void
  onConfirm: () => void
  onCancel: () => void
}

const DEFAULT_PRICE = "$142.49/mo"
const DEFAULT_ERROR = "Couldn't update. Check payment method or try again."
const DEFAULT_TIER_NOTE = {
  before: "Pro tier max.",
  cta: "Upgrade to Plus",
  after: "for up to 250.",
}

export function StepperCard({
  label,
  value,
  displayValue,
  state = "default",
  price = DEFAULT_PRICE,
  errorMessage = DEFAULT_ERROR,
  tierMaxNote = DEFAULT_TIER_NOTE,
  className,
  onClick,
  onConfirm,
  onCancel,
}: StepperCardData) {

  const editing = state === "changed" || state === "saving" || state === "error"
  const disabled = state === "disabled"

  return (
    <div
      className={cn(
        "tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:bg-card tw:px-4 tw:py-3",
        state === "changed" || state === "saving"
          ? "tw:border-2 tw:border-primary"
          : state === "error"
            ? "tw:border-2 tw:border-destructive/40"
            : "tw:border tw:border-border",
        className
      )}
    >
      <span className="tw:text-sm tw:text-muted-foreground">{label}</span>

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:text-sm tw:font-medium">{displayValue ?? value}</span>
        {editing ? (
          <span className="tw:text-xs tw:text-muted-foreground">{price}</span>
        ) : null}
        <ButtonGroup>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={disabled}
            aria-label={`Decrease ${label}`}
            onClick={() => {
              if (typeof value === "string") {
                value = parseInt(value, 10);
              }
              if (value - 1 < 0) {
                return;
              }
              onClick(value - 1);
            }}
          >
            <Minus />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={disabled || state === "tierMax"}
            aria-label={`Increase ${label}`}
            onClick={() => {
              if (typeof value === "string") {
                value = parseInt(value, 10);
              }
              onClick(value + 1);
            }}
          >
            <Plus />
          </Button>
        </ButtonGroup>
      </div>

      {state === "tierMax" ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          {tierMaxNote.before}{" "}
          <button
            type="button"
            className="tw:text-primary tw:hover:underline"
          >
            {tierMaxNote.cta}
          </button>{" "}
          {tierMaxNote.after}
        </p>
      ) : null}

      {state === "error" ? (
        <p className="tw:text-xs tw:text-destructive">{errorMessage}</p>
      ) : null}

      {editing ? (
        <div className="tw:flex tw:items-center tw:justify-end tw:gap-1.5">
          <Button
            variant="outline"
            size="sm"
            disabled={state === "saving"}
            className="tw:text-xs"
            onClick={onCancel}
          >
            Cancel
          </Button>
          {state === "saving" ? (
            <Button size="sm" loading className="tw:text-xs">
              Saving...
            </Button>
          ) : state === "error" ? (
            <Button size="sm" className="tw:text-xs" disabled>
              Save changes
            </Button>
          ) : (
            <Button size="sm" className="tw:text-xs" onClick={onConfirm}>
              Save changes
            </Button>
          )}
        </div>
      ) : null}
    </div>
  )
}
